/**
 * AES-GCM encryption for redux-persist storage.
 * Session key lives in sessionStorage only — cleared when browser closes.
 * localStorage data is unreadable without the active session key.
 */

const SESSION_KEY_NAME = 'tapestry_persist_key';
const ALGO = { name: 'AES-GCM', length: 256 };

function u8ToBase64(buf: Uint8Array): string {
  let s = '';
  buf.forEach(b => { s += String.fromCharCode(b); });
  return btoa(s);
}

function base64ToU8(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0));
}

async function getOrCreateKey(): Promise<CryptoKey> {
  const stored = sessionStorage.getItem(SESSION_KEY_NAME);
  if (stored) {
    try {
      return await crypto.subtle.importKey('raw', base64ToU8(stored), ALGO, false, ['encrypt', 'decrypt']);
    } catch { /* fall through */ }
  }
  const key = await crypto.subtle.generateKey(ALGO, true, ['encrypt', 'decrypt']);
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', key));
  sessionStorage.setItem(SESSION_KEY_NAME, u8ToBase64(raw));
  return key;
}

async function encrypt(plaintext: string): Promise<string> {
  const key  = await getOrCreateKey();
  const iv   = crypto.getRandomValues(new Uint8Array(12));
  const ct   = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plaintext)));
  const out  = new Uint8Array(12 + ct.byteLength);
  out.set(iv);
  out.set(ct, 12);
  return u8ToBase64(out);
}

async function decrypt(ciphertext: string): Promise<string | null> {
  try {
    const key = await getOrCreateKey();
    const buf = base64ToU8(ciphertext);
    const pt  = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buf.slice(0, 12) }, key, buf.slice(12));
    return new TextDecoder().decode(pt);
  } catch {
    return null;
  }
}

export const encryptedStorage = {
  async getItem(key: string): Promise<string | null> {
    const val = localStorage.getItem(key);
    if (!val) return null;
    // Unencrypted legacy data — pass through so existing sessions survive upgrade
    if (val.startsWith('{') || val.startsWith('[') || val.startsWith('"')) return val;
    return await decrypt(val);
  },

  async setItem(key: string, val: string): Promise<void> {
    try {
      localStorage.setItem(key, await encrypt(val));
    } catch {
      localStorage.setItem(key, val);
    }
  },

  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
};
